import { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { NavigationGuard, RawLocation } from 'vue-router';
import { AuthService } from './services/auth.service';
import store from '../../store';
import { AuthStrategyType, RouteAccess } from '../../tenants/types';
import { consumeRetryMarker, isUnauthorized, markAsRetry, RetriableRequestConfig } from './auth-interceptors';
import { AuthStrategy, AuthStrategyOptions, routeAccessOf } from './types';

interface QueuedRequest {
    resolve: (value?: any) => void;
    reject: (reason?: any) => void;
}

/**
 * Acceso con usuario/contraseña (OAuth2 `grant_type=password`).
 *
 * Envuelve la lógica que ya existía, sin cambiar su comportamiento:
 * - tokens y `userInfo` en el módulo Vuex `auth` (`core/auth/store/auth.ts`, persistido en `localStorage`);
 * - llamadas a `/o/token/` y `/me/` en `core/auth/services/auth.service.ts`;
 * - Bearer del store en cada request (`applyAuthorization`) y refresh ante 401 con cola de requests pendientes
 *   (`handleUnauthorized`). No registra interceptores: los instala `AccessCoordinator` y delega acá cuando la ruta
 *   activa es de sesión (o pública, en un tenant con sesión);
 * - guard de las rutas `RouteAccess.Session` / `Public` (antes en `router.ts`).
 */
export class LoginStrategy implements AuthStrategy {
    readonly type = AuthStrategyType.Login;
    readonly supportsLogout = true;

    // Evita varios refresh simultáneos
    private isRefreshing = false;
    private failedQueue: QueuedRequest[] = [];

    constructor(private readonly options: AuthStrategyOptions & { loginPath: string; landingPath: string }) {}

    /** Bearer del access token del store (si hay sesión). Convierte también la marca de reintento en `_retry`. */
    applyAuthorization(config: AxiosRequestConfig): AxiosRequestConfig {
        consumeRetryMarker(config);
        const token = this.getToken();
        if (token) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    }

    getToken(): string | null {
        return store.getters['auth/accessToken'];
    }

    isAuthenticated(): boolean {
        return store.getters['auth/isAuthenticated'];
    }

    async handleUnauthorized(error: AxiosError, http: AxiosInstance): Promise<any> {
        const originalRequest: RetriableRequestConfig = error.config;

        // Si es 401 y todavía no se reintentó
        if (isUnauthorized(error) && !originalRequest._retry) {
            if (this.isRefreshing) {
                // Si ya hay un refresh en curso, encolar esta request
                return new Promise((resolve, reject) => {
                    this.failedQueue.push({ resolve, reject });
                })
                    .then(token => {
                        if (originalRequest.headers) {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                        }
                        return http(markAsRetry(originalRequest));
                    })
                    .catch(err => {
                        return Promise.reject(err);
                    });
            }

            originalRequest._retry = true;
            this.isRefreshing = true;

            const refreshToken = store.getters['auth/refreshToken'];

            if (!refreshToken) {
                // Sin refresh token: logout
                store.dispatch('auth/logout');
                this.processQueue(error, null);
                window.location.href = this.options.loginPath;
                return Promise.reject(error);
            }

            try {
                const response = await AuthService.refreshToken(refreshToken);
                const newAccessToken = response.access_token;
                const expiresIn = response.expires_in;

                // Actualiza el store con el nuevo token
                store.dispatch('auth/updateToken', {
                    accessToken: newAccessToken,
                    expiresIn
                });

                // Sin header por defecto: `applyAuthorization` toma el token nuevo del store en cada request.

                // Procesa las requests encoladas
                this.processQueue(null, newAccessToken);

                // Reintenta la request original (marcada: si vuelve con 401 no se refresca de nuevo)
                if (originalRequest.headers) {
                    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                }
                return http(markAsRetry(originalRequest));
            } catch (refreshError) {
                // Falló el refresh: logout
                this.processQueue(refreshError, null);
                store.dispatch('auth/logout');
                window.location.href = this.options.loginPath;
                return Promise.reject(refreshError);
            } finally {
                this.isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }

    guard: NavigationGuard = (to, from, next) => {
        const { loginPath, landingPath, rootPath } = this.options;
        const isAuthenticated = this.isAuthenticated();
        // Decide el acceso de la ruta destino (`meta.access`), no un `requiresAuth` heredado del layout.
        const requiresAuth = routeAccessOf(to) === RouteAccess.Session;

        if (requiresAuth && !isAuthenticated) {
            // La ruta requiere sesión y no la hay: al login
            next({
                path: loginPath,
                query: { redirect: to.fullPath }
            });
        } else if (to.path === loginPath && isAuthenticated) {
            // Ya autenticado intentando ir al login
            next(landingPath);
        } else if (to.path === rootPath && isAuthenticated) {
            // Autenticado entrando a la raíz
            next(landingPath);
        } else {
            next();
        }
    };

    logout(): RawLocation | null {
        store.dispatch('auth/logout');
        return this.options.loginPath;
    }

    private processQueue(error: any, token: string | null = null): void {
        this.failedQueue.forEach(prom => {
            if (error) {
                prom.reject(error);
            } else {
                prom.resolve(token);
            }
        });

        this.failedQueue = [];
    }
}
